export class TableRenderer {
  constructor(devtools) {
    this.devtools = devtools;
  }

  createErrorTable(errors) {
    const table = document.createElement('div');
    table.className = 'devtools-table';
    
    const header = this.createTableHeader([
      { key: 'severity', label: '⚠️', width: '40px' },
      { key: 'timestamp', label: 'Time', width: '80px' },
      { key: 'type', label: 'Type', width: '80px' },
      { key: 'message', label: 'Message', width: 'auto' },
      { key: 'source', label: 'Source', width: '120px' },
      { key: 'actions', label: 'Actions', width: '100px' }
    ]);
    
    const body = this.createTableBody(errors, this.renderErrorRow.bind(this));
    
    table.appendChild(header);
    table.appendChild(body);
    
    return table;
  }

  createAnalysisTable(analysis) {
    const table = document.createElement('div');
    table.className = 'devtools-analysis-table';
    
    // Severity section
    const severitySection = this.createSection('Severity Analysis', [
      { label: 'Level', value: analysis.severity?.level || 'unknown' },
      { label: 'Score', value: `${analysis.severity?.score || 0}/10` },
      { label: 'Category', value: `${analysis.category?.type} - ${analysis.category?.subtype}` }
    ]);
    
    // Potential causes section
    const causesSection = this.createCausesSection(analysis.potentialCauses || []);
    
    // Suggestions section
    const suggestionsSection = this.createSuggestionsSection(analysis.suggestions || []);
    
    // Related errors section
    const relatedSection = this.createRelatedErrorsSection(analysis.relatedErrors || []);
    
    table.appendChild(severitySection);
    table.appendChild(causesSection);
    table.appendChild(suggestionsSection);
    table.appendChild(relatedSection);
    
    return table;
  }

  createTableHeader(columns) {
    const header = document.createElement('div');
    header.className = 'devtools-table-header';
    
    columns.forEach(col => {
      const cell = document.createElement('div');
      cell.className = 'devtools-table-cell header-cell';
      cell.style.width = col.width;
      cell.textContent = col.label;
      cell.setAttribute('data-key', col.key);
      
      // Add sorting capability
      cell.addEventListener('click', () => this.sortTable(col.key));
      
      header.appendChild(cell);
    });
    
    return header;
  }

  createTableBody(data, rowRenderer) {
    const body = document.createElement('div');
    body.className = 'devtools-table-body';
    
    data.forEach((item, index) => {
      const row = rowRenderer(item, index);
      body.appendChild(row);
    });
    
    return body;
  }

  renderErrorRow(error, index) {
    const row = document.createElement('div');
    row.className = `devtools-table-row ${index % 2 === 0 ? 'even' : 'odd'}`;
    row.setAttribute('data-error-id', error.id);
    
    // Severity indicator
    const severityCell = document.createElement('div');
    severityCell.className = 'devtools-table-cell severity-cell';
    severityCell.innerHTML = this.getSeverityIcon(error.severity);
    
    // Timestamp
    const timeCell = document.createElement('div');
    timeCell.className = 'devtools-table-cell time-cell';
    timeCell.textContent = new Date(error.timestamp).toLocaleTimeString();
    
    // Type
    const typeCell = document.createElement('div');
    typeCell.className = 'devtools-table-cell type-cell';
    typeCell.textContent = error.type?.toUpperCase() || 'UNKNOWN';
    
    // Message (truncated)
    const messageCell = document.createElement('div');
    messageCell.className = 'devtools-table-cell message-cell';
    messageCell.textContent = this.truncateText(error.message || 'No message', 50);
    messageCell.title = error.message; // Full message on hover
    
    // Source
    const sourceCell = document.createElement('div');
    sourceCell.className = 'devtools-table-cell source-cell';
    const sourceText = error.source ? 
      `${this.getFileName(error.source)}:${error.lineno}` : 
      'Unknown';
    sourceCell.textContent = this.truncateText(sourceText, 20);
    sourceCell.title = error.source;
    
    // Actions
    const actionsCell = document.createElement('div');
    actionsCell.className = 'devtools-table-cell actions-cell';
    
    const inspectBtn = document.createElement('button');
    inspectBtn.className = 'devtools-btn-small inspect-btn';
    inspectBtn.textContent = '🔍';
    inspectBtn.title = 'Deep Inspect';
    inspectBtn.addEventListener('click', () => this.inspectError(error));
    
    const fixBtn = document.createElement('button');
    fixBtn.className = 'devtools-btn-small fix-btn';
    fixBtn.textContent = '🔧';
    fixBtn.title = 'Show Fixes';
    fixBtn.addEventListener('click', () => this.showFixes(error));
    
    actionsCell.appendChild(inspectBtn);
    actionsCell.appendChild(fixBtn);
    
    row.appendChild(severityCell);
    row.appendChild(timeCell);
    row.appendChild(typeCell);
    row.appendChild(messageCell);
    row.appendChild(sourceCell);
    row.appendChild(actionsCell);
    
    return row;
  }

  createSection(title, items) {
    const section = document.createElement('div');
    section.className = 'devtools-analysis-section';
    
    const header = document.createElement('div');
    header.className = 'devtools-section-header';
    header.textContent = title;
    
    const content = document.createElement('div');
    content.className = 'devtools-section-content';
    
    items.forEach(item => {
      const row = document.createElement('div');
      row.className = 'devtools-info-row';
      row.innerHTML = `<span class="label">${item.label}:</span> <span class="value">${item.value}</span>`;
      content.appendChild(row);
    });
    
    section.appendChild(header);
    section.appendChild(content);
    
    return section;
  }

  createCausesSection(causes) {
    const section = document.createElement('div');
    section.className = 'devtools-analysis-section';
    
    const header = document.createElement('div');
    header.className = 'devtools-section-header expandable';
    header.textContent = `🔍 Potential Causes (${causes.length})`;
    
    const content = document.createElement('div');
    content.className = 'devtools-section-content collapsed';
    
    causes.forEach(cause => {
      const causeDiv = document.createElement('div');
      causeDiv.className = 'devtools-cause-item';
      
      causeDiv.innerHTML = `
        <div class="cause-header">
          <span class="cause-type">${cause.type}</span>
          <span class="cause-likelihood">${Math.round(cause.likelihood * 100)}% likely</span>
        </div>
        <div class="cause-description">${cause.description}</div>
        <div class="cause-fixes">
          <strong>Suggested fixes:</strong>
          <ul>
            ${cause.fixes.map(fix => `<li>${fix}</li>`).join('')}
          </ul>
        </div>
      `;
      
      content.appendChild(causeDiv);
    });
    
    header.addEventListener('click', () => {
      header.classList.toggle('expanded');
      content.classList.toggle('collapsed');
    });
    
    section.appendChild(header);
    section.appendChild(content);
    
    return section;
  }

  createSuggestionsSection(suggestions) {
    const section = document.createElement('div');
    section.className = 'devtools-analysis-section';
    
    const header = document.createElement('div');
    header.className = 'devtools-section-header expandable';
    header.textContent = `💡 Suggestions (${suggestions.length})`;
    
    const content = document.createElement('div');
    content.className = 'devtools-section-content collapsed';
    
    suggestions.forEach(suggestion => {
      const suggestionDiv = document.createElement('div');
      suggestionDiv.className = 'devtools-suggestion-item';
      
      suggestionDiv.innerHTML = `
        <div class="suggestion-header">
          <span class="suggestion-title">${suggestion.title}</span>
          <span class="suggestion-priority priority-${suggestion.priority}">${suggestion.priority}</span>
        </div>
        <div class="suggestion-description">${suggestion.description}</div>
        <div class="suggestion-code">
          <pre><code>${suggestion.code}</code></pre>
          <button class="copy-btn" onclick="navigator.clipboard.writeText('${suggestion.code.replace(/'/g, "\\'")}')">📋</button>
        </div>
      `;
      
      content.appendChild(suggestionDiv);
    });
    
    header.addEventListener('click', () => {
      header.classList.toggle('expanded');
      content.classList.toggle('collapsed');
    });
    
    section.appendChild(header);
    section.appendChild(content);
    
    return section;
  }

  createRelatedErrorsSection(relatedErrors) {
    const section = document.createElement('div');
    section.className = 'devtools-analysis-section';
    
    const header = document.createElement('div');
    header.className = 'devtools-section-header expandable';
    header.textContent = `🔗 Related Errors (${relatedErrors.length})`;
    
    const content = document.createElement('div');
    content.className = 'devtools-section-content collapsed';
    
    relatedErrors.forEach(related => {
      const relatedDiv = document.createElement('div');
      relatedDiv.className = 'devtools-related-item';
      
      relatedDiv.innerHTML = `
        <div class="related-header">
          <span class="related-relationship">${related.relationship}</span>
          <span class="related-confidence">${Math.round(related.confidence * 100)}% match</span>
        </div>
        <div class="related-message">${this.truncateText(related.error.message, 80)}</div>
        <div class="related-time">${new Date(related.error.timestamp).toLocaleString()}</div>
      `;
      
      relatedDiv.addEventListener('click', () => {
        this.devtools.windowManager.createErrorWindow(related.error);
      });
      
      content.appendChild(relatedDiv);
    });
    
    header.addEventListener('click', () => {
      header.classList.toggle('expanded');
      content.classList.toggle('collapsed');
    });
    
    section.appendChild(header);
    section.appendChild(content);
    
    return section;
  }

  getSeverityIcon(severity) {
    if (!severity) return '❓';
    
    switch (severity.level) {
      case 'critical': return '🔴';
      case 'high': return '🟠';
      case 'medium': return '🟡';
      case 'low': return '🟢';
      default: return '❓';
    }
  }

  truncateText(text, maxLength) {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength - 3) + '...';
  }

  getFileName(path) {
    if (!path) return 'unknown';
    return path.split('/').pop() || path;
  }

  inspectError(error) {
    // Trigger deep inspection
    this.devtools.windowManager.createInspectionWindow(error);
  }

  showFixes(error) {
    // Show quick fixes window
    this.devtools.windowManager.createFixesWindow(error);
  }

  sortTable(key) {
    // Implement table sorting
    console.log(`Sorting by ${key}`);
  }
}